import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface PolicyAckStats {
  totalStaff: number;
  acknowledgedCount: number;
  pendingCount: number;
  pendingNames: string[];
  isFullyAcknowledged: boolean;
}

/**
 * Fetches per-policy acknowledgement statistics for all approved/locked policies
 * that require acknowledgement in the current user's clinic.
 * Returns a Map<policyId, PolicyAckStats>.
 */
export function useAckStats() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['ack-stats', profile?.clinic_id],
    queryFn: async () => {
      if (!profile?.clinic_id) return new Map<string, PolicyAckStats>();

      // 1. Get all staff in clinic
      const { data: staffProfiles, error: staffError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('clinic_id', profile.clinic_id);

      if (staffError) throw staffError;
      if (!staffProfiles?.length) return new Map<string, PolicyAckStats>();

      const totalStaff = staffProfiles.length;
      const staffNameMap = new Map(staffProfiles.map(s => [s.id, s.full_name]));

      // 2. Get policies requiring acknowledgement
      const { data: policies, error: polError } = await supabase
        .from('policies')
        .select('id')
        .eq('clinic_id', profile.clinic_id)
        .eq('requires_acknowledgement', true)
        .eq('status', 'Approved')
        .or('is_archived.is.null,is_archived.eq.false');

      if (polError) throw polError;
      if (!policies?.length) return new Map<string, PolicyAckStats>();

      const policyIds = policies.map(p => p.id);

      // 3. Get latest version per policy
      const { data: versions, error: verError } = await supabase
        .from('policy_versions')
        .select('id, policy_id, version_number')
        .in('policy_id', policyIds)
        .order('version_number', { ascending: false });

      if (verError) throw verError;
      if (!versions?.length) return new Map<string, PolicyAckStats>();

      const latestVersions = new Map<string, string>(); // policyId -> versionId
      versions.forEach(v => {
        if (!latestVersions.has(v.policy_id)) {
          latestVersions.set(v.policy_id, v.id);
        }
      });

      // 4. Get all acknowledgements for these versions
      const versionIds = Array.from(latestVersions.values());
      const { data: acks, error: ackError } = await supabase
        .from('policy_acknowledgements')
        .select('policy_version_id, acknowledged_by')
        .in('policy_version_id', versionIds);

      if (ackError) throw ackError;

      // 5. Build stats per policy
      const acksPerVersion = new Map<string, Set<string>>();
      (acks ?? []).forEach(a => {
        if (!acksPerVersion.has(a.policy_version_id)) {
          acksPerVersion.set(a.policy_version_id, new Set());
        }
        acksPerVersion.get(a.policy_version_id)!.add(a.acknowledged_by);
      });

      const result = new Map<string, PolicyAckStats>();
      latestVersions.forEach((versionId, policyId) => {
        const ackedBy = acksPerVersion.get(versionId) ?? new Set();
        const acknowledgedCount = ackedBy.size;
        const pendingNames: string[] = [];
        staffProfiles.forEach(s => {
          if (!ackedBy.has(s.id)) {
            pendingNames.push(s.full_name);
          }
        });

        result.set(policyId, {
          totalStaff,
          acknowledgedCount,
          pendingCount: totalStaff - acknowledgedCount,
          pendingNames,
          isFullyAcknowledged: acknowledgedCount >= totalStaff,
        });
      });

      return result;
    },
    enabled: !!profile?.clinic_id,
    staleTime: 30000,
  });
}
